import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Label } from '../ui/label';
import { ImagePlus, Trash2, ZoomIn, Move, RotateCcw, Loader2 } from 'lucide-react';

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

/**
 * Downscale an uploaded image (preserving its aspect ratio) and return a
 * compact JPEG data-URI so it can be stored inline in the document.
 */
function downscaleImage(file, maxDim = 900, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(1, maxDim / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Read-only renderer that displays an image with a fixed aspect ratio and the
 * stored framing (object-position + zoom). Used on cards/tiles.
 */
export const CroppedImage = ({ data, aspect = '4 / 3', className = '', style = {} }) => {
  const v = data || {};
  if (!v.image) return null;
  return (
    <div className={`relative overflow-hidden bg-muted ${className}`} style={{ aspectRatio: aspect, ...style }}>
      <img
        src={v.image}
        alt=""
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover"
        style={{
          objectPosition: `${v.image_x ?? 50}% ${v.image_y ?? 50}%`,
          transform: `scale(${v.image_zoom ?? 1})`,
        }}
      />
    </div>
  );
};

/**
 * Upload + framing control. The aspect ratio is fixed (same proportions for
 * every item); the user can pan (drag) and zoom to choose the framing.
 *
 * value: { image, image_x, image_y, image_zoom, ...rest }
 * onChange returns the full merged object (other keys preserved).
 */
export const ImageCropUploader = ({
  value,
  onChange,
  aspect = '4 / 3',
  label,
  hint,
  testId = 'image-uploader',
}) => {
  const { t } = useTranslation();
  const resolvedLabel = label ?? t('imgUploader.defaultLabel');
  const resolvedHint = hint ?? t('imgUploader.hint');
  const v = value || {};
  const image = v.image || null;
  const x = v.image_x ?? 50;
  const y = v.image_y ?? 50;
  const zoom = v.image_zoom ?? 1;

  const fileRef = useRef(null);
  const boxRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const emit = (patch) => onChange({ ...v, image, image_x: x, image_y: y, image_zoom: zoom, ...patch });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const dataUrl = await downscaleImage(file);
      onChange({ ...v, image: dataUrl, image_x: 50, image_y: 50, image_zoom: 1 });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('image load error', err);
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onPointerDown = (e) => {
    if (!image || !boxRef.current) return;
    e.preventDefault();
    const rect = boxRef.current.getBoundingClientRect();
    const startPx = e.clientX;
    const startPy = e.clientY;
    const startX = x;
    const startY = y;
    const onMove = (ev) => {
      const dx = ev.clientX - startPx;
      const dy = ev.clientY - startPy;
      const nx = clamp(startX - (dx / rect.width) * 100, 0, 100);
      const ny = clamp(startY - (dy / rect.height) * 100, 0, 100);
      onChange({ ...v, image, image_zoom: zoom, image_x: nx, image_y: ny });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  return (
    <div data-testid={testId}>
      {resolvedLabel && <Label className="mb-1.5 block">{resolvedLabel}</Label>}

      {!image ? (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          data-testid={`${testId}-empty`}
          className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-muted/40 py-8 text-muted-foreground transition-colors hover:border-primary/50 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          style={{ aspectRatio: aspect }}
        >
          {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : <ImagePlus className="h-6 w-6" />}
          <span className="text-sm font-medium">{loading ? t('imgUploader.loading') : t('imgUploader.upload')}</span>
          <span className="text-xs">{t('imgUploader.formats')}</span>
        </button>
      ) : (
        <div className="space-y-2.5">
          <div
            ref={boxRef}
            onPointerDown={onPointerDown}
            data-testid={`${testId}-frame`}
            className="relative cursor-move select-none overflow-hidden rounded-xl border border-border bg-muted"
            style={{ aspectRatio: aspect, touchAction: 'none' }}
          >
            <img
              src={image}
              alt={t('imgUploader.previewAlt')}
              draggable={false}
              className="pointer-events-none absolute inset-0 h-full w-full object-cover"
              style={{ objectPosition: `${x}% ${y}%`, transform: `scale(${zoom})` }}
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center gap-1 bg-gradient-to-t from-black/55 to-transparent px-2 py-1.5 text-[11px] font-medium text-white">
              <Move className="h-3.5 w-3.5" /> {resolvedHint}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <ZoomIn className="h-4 w-4 shrink-0 text-muted-foreground" />
            <Slider
              value={[zoom]}
              min={1}
              max={3}
              step={0.02}
              onValueChange={(val) => emit({ image_zoom: val[0] })}
              data-testid={`${testId}-zoom`}
              aria-label={t('imgUploader.zoomAria')}
            />
          </div>

          <div className="flex items-center gap-2">
            <Button type="button" size="sm" variant="outline" className="rounded-lg" onClick={() => fileRef.current?.click()}>
              <ImagePlus className="mr-1.5 h-4 w-4" /> {t('imgUploader.change')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="rounded-lg"
              onClick={() => emit({ image_x: 50, image_y: 50, image_zoom: 1 })}
              title={t('imgUploader.resetFraming')}
            >
              <RotateCcw className="mr-1.5 h-4 w-4" /> {t('imgUploader.reset')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="ml-auto rounded-lg text-destructive hover:text-destructive"
              onClick={() => onChange({ ...v, image: null, image_x: 50, image_y: 50, image_zoom: 1 })}
              data-testid={`${testId}-remove`}
            >
              <Trash2 className="mr-1.5 h-4 w-4" /> {t('imgUploader.remove')}
            </Button>
          </div>
        </div>
      )}

      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`${testId}-input`} />
    </div>
  );
};
